<template>
  <main class="admin-menu afro-bg">
    <section class="admin-shell paper-panel">
      <header class="admin-head">
        <div>
          <p class="eyebrow">Juliam Arcade</p>
          <h1>Catálogo y recetas</h1>
          <span>Administra disponibilidad, orden, recetas y combos del menú de Juliam en tiempo real.</span>
        </div>
        <div class="head-actions">
          <router-link class="afro-btn ghost" to="/menu" target="_blank">Ver menú</router-link>
          <button class="afro-btn" type="button" @click="loadMenu">Actualizar</button>
        </div>
      </header>

      <div v-if="loading" class="state-card">Cargando catalogo...</div>
      <div v-else class="admin-grid">
        <aside class="category-panel afro-section-card">
          <div class="panel-title">
            <div>
              <h2>Categorias</h2>
              <small class="drag-help">Arrastra desde el control para ordenar</small>
            </div>
            <button class="afro-btn small" type="button" @click="newCategory">Nueva</button>
          </div>

          <draggable
            v-model="categories"
            item-key="id_token"
            handle=".drag-handle"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            class="category-list"
            @end="persistCategoryOrder"
          >
            <template #item="{ element: cat }">
              <article
                class="catalog-pill category-pill"
                :class="{ active: selectedCategoryToken === cat.id_token, off: !cat.active }"
                role="button"
                tabindex="0"
                @click="selectCategory(cat)"
                @keydown.enter="selectCategory(cat)"
              >
                <button class="drag-handle" type="button" title="Arrastrar categoria" aria-label="Arrastrar categoria" @click.stop>&#8942;&#8942;</button>
                <div class="pill-copy">
                  <strong>{{ cat.badge_label || cat.name }}</strong>
                  <small>{{ cat.products.length }} productos &bull; {{ categoryLocationLabel(cat) }}</small>
                </div>
                <label class="binary-switch" :class="{ busy: isPending(pendingCategoryTokens, cat.id_token) }" @click.stop>
                  <input
                    type="checkbox"
                    :checked="Boolean(Number(cat.active))"
                    :disabled="isPending(pendingCategoryTokens, cat.id_token)"
                    :aria-label="cat.active ? 'Ocultar categoria' : 'Mostrar categoria'"
                    @change="toggleCategoryCard(cat, $event.target.checked)"
                  />
                  <span class="switch-track"><span class="switch-thumb"></span></span>
                  <span class="switch-text">{{ cat.active ? 'Visible' : 'Oculta' }}</span>
                </label>
              </article>
            </template>
          </draggable>
        </aside>

        <section class="editor-stack">
          <article class="editor-card afro-section-card">
            <div class="panel-title">
              <h2>{{ categoryForm.id_token ? 'Editar categoria' : 'Nueva categoria' }}</h2>
              <button v-if="categoryForm.id_token" class="afro-btn ghost small" type="button" @click="newProduct">Nuevo producto</button>
            </div>
            <div class="form-grid cols-3">
              <label><span class="afro-label">Nombre</span><input v-model="categoryForm.name" class="afro-input" /></label>
              <label><span class="afro-label">Bandera</span><input v-model="categoryForm.badge_label" class="afro-input" /></label>
              <label><span class="afro-label">Slug</span><input v-model="categoryForm.slug" class="afro-input" /></label>
              <label><span class="afro-label">Formato</span><select v-model="categoryForm.layout_type" class="afro-select"><option v-for="option in layoutOptions" :key="option.value" :value="option.value">{{ option.label }}</option></select></label>
              <label>
                <span class="afro-label">Ubicacion</span>
                <select v-model="categoryForm.section_key" class="afro-select">
                  <option value="left">Panel principal</option>
                  <option value="right">Panel secundario</option>
                  <option value="bottom">Franja inferior</option>
                  <option value="full">Ancho completo</option>
                </select>
              </label>
              <label v-if="categoryForm.section_key === 'full'">
                <span class="afro-label">Columnas de productos</span>
                <select v-model.number="categoryForm.product_columns" class="afro-select">
                  <option :value="1">Una columna</option>
                  <option :value="2">Dos columnas</option>
                </select>
              </label>
              <label><span class="afro-label">Icono</span><select v-model="categoryForm.icon_key" class="afro-select"><option v-for="option in iconOptions" :key="option" :value="option">{{ option }}</option></select></label>
              <label><span class="afro-label">Asset</span><input v-model="categoryForm.asset_key" class="afro-input" placeholder="dessert, dog, qr" /></label>
              <label class="check-row"><input v-model="categoryForm.active" type="checkbox" /> Categoria visible</label>
              <label class="check-row"><input v-model="categoryForm.reward_active" type="checkbox" /> Canjeable en wallet</label>
            </div>
            <label><span class="afro-label">Descripcion interna</span><textarea v-model="categoryForm.description" class="afro-textarea" /></label>
            <div class="button-row">
              <button class="afro-btn" type="button" :disabled="saving" @click="saveCategory">Guardar categoria</button>
              <button v-if="categoryForm.id_token" class="afro-btn danger" type="button" @click="removeCategory">Eliminar</button>
            </div>
          </article>

          <article v-if="selectedCategory" class="editor-card afro-section-card">
            <div class="panel-title">
              <div>
                <h2>Productos de {{ selectedCategory.name }}</h2>
                <small class="drag-help">Arrastra para cambiar el orden dentro de la categoria</small>
              </div>
              <button class="afro-btn small" type="button" @click="newProduct">Agregar producto</button>
            </div>

            <div class="products-layout">
              <draggable
                :list="selectedCategory.products"
                item-key="id_token"
                handle=".drag-handle"
                ghost-class="drag-ghost"
                chosen-class="drag-chosen"
                class="product-list"
                @end="persistProductOrder"
              >
                <template #item="{ element: product }">
                  <article
                    class="catalog-pill product-pill"
                    :class="{
                      active: selectedProductToken === product.id_token,
                      off: product.availability_status === 'hidden',
                      soldout: product.availability_status === 'sold_out'
                    }"
                    role="button"
                    tabindex="0"
                    @click="selectProduct(product)"
                    @keydown.enter="selectProduct(product)"
                  >
                    <button class="drag-handle" type="button" title="Arrastrar producto" aria-label="Arrastrar producto" @click.stop>&#8942;&#8942;</button>
                    <div class="pill-copy">
                      <strong>{{ product.name }}</strong>
                      <small>{{ productPriceLabel(product) }}</small>
                    </div>
                    <div class="status-switch" role="group" :aria-label="`Disponibilidad de ${product.name}`" @click.stop>
                      <button
                        v-for="status in productStatuses"
                        :key="status.value"
                        type="button"
                        :class="[status.value, { selected: product.availability_status === status.value }]"
                        :disabled="isPending(pendingProductTokens, product.id_token)"
                        :aria-pressed="product.availability_status === status.value"
                        @click="setProductStatusCard(product, status.value)"
                      >{{ status.short }}</button>
                    </div>
                  </article>
                </template>
              </draggable>

              <div class="product-editor">
                <h3>{{ productForm.id_token ? 'Editar producto' : 'Nuevo producto' }}</h3>
                <div class="form-grid cols-2">
                  <label><span class="afro-label">Producto</span><input v-model="productForm.name" class="afro-input" /></label>
                  <label>
                    <span class="afro-label">Estado en el menu</span>
                    <select v-model="productForm.availability_status" class="afro-select">
                      <option v-for="status in productStatuses" :key="status.value" :value="status.value">{{ status.label }}</option>
                    </select>
                  </label>
                  <label><span class="afro-label">Etiqueta de precio</span><input v-model="productForm.price_label" class="afro-input" placeholder="Opcional" /></label>
                  <label><span class="afro-label">Precio caliente</span><input v-model="productForm.price_hot" class="afro-input" type="number" step="0.01" /></label>
                  <label><span class="afro-label">Precio frio</span><input v-model="productForm.price_cold" class="afro-input" type="number" step="0.01" /></label>
                  <label><span class="afro-label">Precio unico</span><input v-model="productForm.price" class="afro-input" type="number" step="0.01" /></label>
                  <label><span class="afro-label">Icono</span><input v-model="productForm.icon_key" class="afro-input" placeholder="snow, coffee" /></label>
                  <label class="check-row"><input v-model="productForm.has_hot" type="checkbox" /> Tiene caliente</label>
                  <label class="check-row"><input v-model="productForm.has_cold" type="checkbox" /> Tiene frio</label>
                  <label class="check-row"><input v-model="productForm.reward_active" type="checkbox" /> Canjeable con puntos</label>
                  <label><span class="afro-label">Puntos para canje (opcional)</span><input v-model="productForm.reward_points_price" class="afro-input" type="number" min="0" step="0.01" placeholder="En blanco: usa precio del menu" /><small class="field-help">Dejalo vacio para sincronizar automaticamente los cambios de precio.</small></label>
                  <label><span class="afro-label">Stock para canje</span><input v-model="productForm.reward_stock_quantity" class="afro-input" type="number" step="1" placeholder="Sin limite" /></label>
                  <label><span class="afro-label">ID catalogo Gestion Total</span><input v-model="productForm.gt_catalog_product_id" class="afro-input" type="number" step="1" placeholder="Opcional" /></label>
                </div>
                <label><span class="afro-label">Descripcion</span><textarea v-model="productForm.description" class="afro-textarea" /></label>
                <div class="button-row">
                  <button class="afro-btn" type="button" :disabled="saving" @click="saveProduct">Guardar producto</button>
                  <button v-if="productForm.id_token" class="afro-btn danger" type="button" @click="removeProduct">Eliminar</button>
                </div>
              </div>
            </div>
          </article>

          <article v-if="selectedProductToken" class="editor-card afro-section-card recipe-card">
            <div class="panel-title">
              <h2>Receta: {{ productForm.name }}</h2>
              <button class="afro-btn small" type="button" @click="addRecipeItem">Agregar ingrediente</button>
            </div>
            <div class="recipe-table">
              <div class="recipe-head"><span>Cantidad</span><span>Medida</span><span>Producto</span><span></span></div>
              <div v-for="(item, index) in recipeForm.items" :key="index" class="recipe-row">
                <input v-model="item.quantity" class="afro-input" type="number" step="0.001" />
                <input v-model="item.measure" class="afro-input" placeholder="ml, g, oz" />
                <input v-model="item.item_name" class="afro-input" placeholder="Ingrediente" />
                <button class="afro-btn danger small" type="button" @click="removeRecipeItem(index)">Quitar</button>
              </div>
            </div>
            <label><span class="afro-label">Detalles de preparacion</span><textarea v-model="recipeForm.preparation_details" class="afro-textarea big" placeholder="Pasos, notas de servicio, decoracion, etc." /></label>
            <div class="button-row">
              <button class="afro-btn" type="button" :disabled="saving" @click="saveRecipeData">Guardar receta</button>
            </div>
          </article>

          <article class="editor-card afro-section-card feature-admin-card">
            <div class="panel-title">
              <div>
                <h2>Cards de promociones</h2>
                <p class="helper-text">Agrega todas las cards que necesites. En escritorio rotan en el carrusel; en celular aparecen todas.</p>
                <small class="drag-help">Arrastra para definir el orden del carrusel.</small>
              </div>
              <button class="afro-btn small" type="button" @click="newFeature">Nueva card</button>
            </div>

            <div class="feature-layout-admin">
              <draggable
                v-model="features"
                item-key="id_token"
                handle=".drag-handle"
                ghost-class="drag-ghost"
                chosen-class="drag-chosen"
                class="feature-list"
                @end="persistFeatureOrder"
              >
                <template #item="{ element: feature }">
                  <article
                    class="catalog-pill feature-pill"
                    :class="{ active: selectedFeatureToken === feature.id_token, off: !feature.active }"
                    role="button"
                    tabindex="0"
                    @click="selectFeature(feature)"
                    @keydown.enter="selectFeature(feature)"
                  >
                    <button class="drag-handle" type="button" title="Arrastrar promocion" aria-label="Arrastrar promocion" @click.stop>&#8942;&#8942;</button>
                    <div class="pill-copy">
                      <strong>{{ feature.name }}</strong>
                      <small>{{ featurePriceLabel(feature) }} &bull; {{ feature.display_duration_seconds }} s</small>
                    </div>
                    <label class="binary-switch" :class="{ busy: isPending(pendingFeatureTokens, feature.id_token) }" @click.stop>
                      <input
                        type="checkbox"
                        :checked="Boolean(Number(feature.active))"
                        :disabled="isPending(pendingFeatureTokens, feature.id_token)"
                        :aria-label="feature.active ? 'Ocultar promocion' : 'Mostrar promocion'"
                        @change="toggleFeatureCard(feature, $event.target.checked)"
                      />
                      <span class="switch-track"><span class="switch-thumb"></span></span>
                      <span class="switch-text">{{ feature.active ? 'Visible' : 'Oculta' }}</span>
                    </label>
                  </article>
                </template>
                <template #footer>
                  <p v-if="!features.length" class="empty-admin">Agrega una promocion, aviso o novedad.</p>
                </template>
              </draggable>

              <div class="feature-editor">
                <h3>{{ featureForm.id_token ? 'Editar card' : 'Nueva card' }}</h3>
                <div class="form-grid cols-2">
                  <label><span class="afro-label">Nombre</span><input v-model="featureForm.name" class="afro-input" placeholder="Ej. Promocion del dia" /></label>
                  <label><span class="afro-label">Precio</span><input v-model="featureForm.price" class="afro-input" type="number" step="0.01" placeholder="Opcional" /></label>
                  <label><span class="afro-label">Etiqueta de precio</span><input v-model="featureForm.price_label" class="afro-input" placeholder="Ej. 2x1, desde $60" /></label>
                  <label><span class="afro-label">Tiempo en pantalla</span><input v-model.number="featureForm.display_duration_seconds" class="afro-input" type="number" min="3" max="60" step="1" /><small class="field-help">Entre 3 y 60 segundos.</small></label>
                  <label class="check-row"><input v-model="featureForm.active" type="checkbox" /> Card visible</label>
                </div>
                <label><span class="afro-label">Descripcion corta</span><textarea v-model="featureForm.description" class="afro-textarea compact" maxlength="255" placeholder="Texto breve para que no se sature el diseno." /></label>

                <div class="photo-grid">
                  <label>
                    <span class="afro-label">Foto</span>
                    <input class="afro-input" type="file" accept="image/png,image/jpeg,image/webp" @change="onFeaturePhotoChange" />
                  </label>
                  <div class="photo-preview" :class="{ empty: !featurePreviewUrl }">
                    <img v-if="featurePreviewUrl" :src="featurePreviewUrl" alt="Vista previa" />
                    <span v-else>Sin foto</span>
                  </div>
                </div>

                <div class="button-row">
                  <button class="afro-btn" type="button" :disabled="saving" @click="saveFeatureData">Guardar card</button>
                  <button v-if="featureForm.id_token && featureForm.image_url" class="afro-btn ghost" type="button" @click="removeFeaturePhoto">Quitar foto</button>
                  <button v-if="featureForm.id_token" class="afro-btn danger" type="button" @click="removeFeature">Eliminar</button>
                </div>
              </div>
            </div>
          </article>
        </section>
      </div>
    </section>
  </main>
</template>

<script>
import draggable from 'vuedraggable'
import {
  getAdminMenu,
  createCategory,
  updateCategory,
  deleteCategory,
  toggleCategory,
  reorderCategories,
  createFeature,
  updateFeature,
  deleteFeature,
  toggleFeature,
  reorderFeatures,
  uploadFeaturePhoto,
  createProduct,
  updateProduct,
  deleteProduct,
  setProductAvailability,
  reorderProducts,
  saveRecipe
} from '@/services/menuApi'
import { connectMenuSocket, closeMenuSocket } from '@/services/menuSocket'

function emptyCategory() {
  return {
    id_token: '', name: '', slug: '', badge_label: '', description: '',
    icon_key: 'gamepad', layout_type: 'single_price', section_key: 'left', product_columns: 1,
    asset_key: '', active: true, reward_active: true
  }
}

function emptyProduct(categoryToken = '') {
  return {
    id_token: '', category_token: categoryToken, name: '', description: '', price_hot: '', price_cold: '', price: '',
    price_label: '', has_hot: false, has_cold: false, serving_label: '', icon_key: '', availability_status: 'visible',
    active: true, reward_active: true, reward_points_price: '', reward_stock_quantity: '', gt_catalog_product_id: ''
  }
}

function emptyFeature() {
  return {
    id_token: '', name: '', description: '', price: '', price_label: '', image_url: '', layout_span: 'auto',
    display_duration_seconds: 8, active: true
  }
}

export default {
  name: 'MenuAdmin',
  components: { draggable },
  data() {
    return {
      loading: true,
      saving: false,
      categories: [],
      features: [],
      selectedCategoryToken: '',
      selectedProductToken: '',
      selectedFeatureToken: '',
      categoryForm: emptyCategory(),
      productForm: emptyProduct(),
      featureForm: emptyFeature(),
      featurePhotoFile: null,
      featurePreviewUrl: '',
      recipeForm: { preparation_details: '', items: [] },
      pendingCategoryTokens: [],
      pendingProductTokens: [],
      pendingFeatureTokens: [],
      realtimeReloadTimer: null,
      socketListener: null,
      layoutOptions: [
        { value: 'beverages_dual', label: 'Bebidas caliente/frio' },
        { value: 'cold_list', label: 'Lista fria o frappe' },
        { value: 'single_price', label: 'Precio unico' },
        { value: 'extras', label: 'Extras' }
      ],
      productStatuses: [
        { value: 'visible', label: 'Visible y disponible', short: 'Visible' },
        { value: 'sold_out', label: 'Visible, pero agotado', short: 'Agotado' },
        { value: 'hidden', label: 'Oculto del menu', short: 'Oculto' }
      ],
      iconOptions: ['gamepad', 'burger', 'drink', 'arcade', 'snack', 'star', 'trophy']
    }
  },
  computed: {
    selectedCategory() {
      return this.categories.find((cat) => cat.id_token === this.selectedCategoryToken) || null
    }
  },
  async created() {
    await this.loadMenu()
    this.socketListener = (payload) => {
      if (payload?.event === 'loyalty.updated') return
      clearTimeout(this.realtimeReloadTimer)
      this.realtimeReloadTimer = setTimeout(() => this.loadMenu(true, true), 250)
    }
    connectMenuSocket(this.socketListener)
  },
  beforeUnmount() {
    clearTimeout(this.realtimeReloadTimer)
    closeMenuSocket(this.socketListener)
    if (this.featurePreviewUrl && this.featurePreviewUrl.startsWith('blob:')) URL.revokeObjectURL(this.featurePreviewUrl)
  },
  methods: {
    async loadMenu(silent = false, preserveEditor = false) {
      if (!silent) this.loading = true
      try {
        const response = await getAdminMenu()
        this.categories = response.categories || []
        this.features = response.features || []

        if (!this.selectedCategoryToken && this.categories.length) this.selectCategory(this.categories[0])
        if (this.selectedCategoryToken) {
          const cat = this.categories.find((item) => item.id_token === this.selectedCategoryToken)
          if (cat) {
            if (preserveEditor) this.categoryForm.active = Boolean(Number(cat.active))
            else this.categoryForm = this.cloneCategory(cat)
          }
          const product = cat && cat.products.find((item) => item.id_token === this.selectedProductToken)
          if (product) {
            if (preserveEditor) {
              this.productForm.availability_status = product.availability_status || 'visible'
              this.productForm.active = Boolean(Number(product.active))
            } else this.selectProduct(product)
          }
        }

        if (this.selectedFeatureToken) {
          const feature = this.features.find((item) => item.id_token === this.selectedFeatureToken)
          if (feature && !this.featurePhotoFile) {
            if (preserveEditor) this.featureForm.active = Boolean(Number(feature.active))
            else this.selectFeature(feature)
          }
        }
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo cargar el catalogo'))
      } finally {
        this.loading = false
      }
    },
    cloneCategory(cat) {
      return {
        id_token: cat.id_token || '',
        name: cat.name || '',
        slug: cat.slug || '',
        badge_label: cat.badge_label || '',
        description: cat.description || '',
        icon_key: cat.icon_key || 'coffee',
        layout_type: cat.layout_type || 'single_price',
        section_key: cat.section_key || 'left',
        product_columns: Number(cat.product_columns) === 2 ? 2 : 1,
        asset_key: cat.asset_key || '',
        active: Boolean(Number(cat.active)),
        reward_active: cat.reward_active === undefined ? true : Boolean(Number(cat.reward_active))
      }
    },
    cloneProduct(product) {
      return {
        id_token: product.id_token || '',
        category_token: product.category_token || this.selectedCategoryToken,
        name: product.name || '',
        description: product.description || '',
        price_hot: product.price_hot === null ? '' : product.price_hot,
        price_cold: product.price_cold === null ? '' : product.price_cold,
        price: product.price === null ? '' : product.price,
        price_label: product.price_label || '',
        has_hot: Boolean(Number(product.has_hot)),
        has_cold: Boolean(Number(product.has_cold)),
        serving_label: product.serving_label || '',
        icon_key: product.icon_key || '',
        availability_status: product.availability_status || (Number(product.active) ? 'visible' : 'hidden'),
        active: Boolean(Number(product.active)),
        reward_active: product.reward_active === undefined ? true : Boolean(Number(product.reward_active)),
        reward_points_price: Number(product.reward_points_price || 0) > 0 ? product.reward_points_price : '',
        reward_stock_quantity: product.reward_stock_quantity === null ? '' : (product.reward_stock_quantity || ''),
        gt_catalog_product_id: product.gt_catalog_product_id === null ? '' : (product.gt_catalog_product_id || '')
      }
    },
    cloneFeature(feature) {
      return {
        id_token: feature.id_token || '',
        name: feature.name || '',
        description: feature.description || '',
        price: feature.price === null ? '' : feature.price,
        price_label: feature.price_label || '',
        image_url: feature.image_url || '',
        layout_span: feature.layout_span || 'auto',
        display_duration_seconds: Number(feature.display_duration_seconds) || 8,
        active: Boolean(Number(feature.active))
      }
    },
    selectCategory(cat) {
      this.selectedCategoryToken = cat.id_token
      this.categoryForm = this.cloneCategory(cat)
      this.newProduct()
    },
    newCategory() {
      this.selectedCategoryToken = ''
      this.selectedProductToken = ''
      this.categoryForm = emptyCategory()
      this.productForm = emptyProduct()
      this.recipeForm = { preparation_details: '', items: [] }
    },
    async saveCategory() {
      this.saving = true
      try {
        const payload = {
          ...this.categoryForm,
          product_columns: Number(this.categoryForm.product_columns) === 2 ? 2 : 1,
          active: this.categoryForm.active ? 1 : 0,
          reward_active: this.categoryForm.reward_active ? 1 : 0
        }
        if (this.categoryForm.id_token) await updateCategory(this.categoryForm.id_token, payload)
        else {
          const response = await createCategory(payload)
          this.selectedCategoryToken = response.id_token
        }
        await this.loadMenu(true)
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar la categoria'))
      } finally {
        this.saving = false
      }
    },
    async removeCategory() {
      if (!this.categoryForm.id_token || !confirm('Eliminar esta categoria y ocultar sus productos?')) return
      await deleteCategory(this.categoryForm.id_token)
      this.newCategory()
      await this.loadMenu(true)
    },
    async toggleCategoryCard(cat, checked) {
      const oldValue = Number(cat.active) === 1
      const nextValue = checked ? 1 : 0
      cat.active = nextValue
      if (this.categoryForm.id_token === cat.id_token) this.categoryForm.active = checked
      this.addPending('pendingCategoryTokens', cat.id_token)
      try {
        await toggleCategory(cat.id_token, nextValue)
      } catch (error) {
        cat.active = oldValue ? 1 : 0
        if (this.categoryForm.id_token === cat.id_token) this.categoryForm.active = oldValue
        alert(this.errorMessage(error, 'No se pudo cambiar la visibilidad'))
      } finally {
        this.removePending('pendingCategoryTokens', cat.id_token)
      }
    },
    async persistCategoryOrder() {
      try {
        await reorderCategories(this.categories.map((cat) => cat.id_token))
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar el orden'))
        await this.loadMenu(true, true)
      }
    },
    newProduct() {
      this.selectedProductToken = ''
      this.productForm = emptyProduct(this.selectedCategoryToken)
      this.recipeForm = { preparation_details: '', items: [] }
    },
    selectProduct(product) {
      this.selectedProductToken = product.id_token
      this.productForm = this.cloneProduct(product)
      this.recipeForm = {
        preparation_details: product.preparation_details || '',
        items: (product.recipe_items || []).map((item) => ({
          quantity: item.quantity === null ? '' : item.quantity,
          measure: item.measure || '',
          item_name: item.item_name || ''
        }))
      }
    },
    async saveProduct() {
      if (!this.selectedCategoryToken) return alert('Selecciona una categoria')
      this.saving = true
      try {
        const status = this.productForm.availability_status || 'visible'
        const payload = {
          ...this.productForm,
          category_token: this.selectedCategoryToken,
          availability_status: status,
          active: status === 'hidden' ? 0 : 1,
          has_hot: this.productForm.has_hot ? 1 : 0,
          has_cold: this.productForm.has_cold ? 1 : 0,
          reward_active: this.productForm.reward_active ? 1 : 0
        }
        if (this.productForm.id_token) await updateProduct(this.productForm.id_token, payload)
        else {
          const response = await createProduct(payload)
          this.selectedProductToken = response.id_token
        }
        await this.loadMenu(true)
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar el producto'))
      } finally {
        this.saving = false
      }
    },
    async removeProduct() {
      if (!this.productForm.id_token || !confirm('Eliminar este producto?')) return
      await deleteProduct(this.productForm.id_token)
      this.newProduct()
      await this.loadMenu(true)
    },
    async setProductStatusCard(product, status) {
      if (product.availability_status === status || this.isPending(this.pendingProductTokens, product.id_token)) return
      const oldStatus = product.availability_status
      const oldActive = product.active
      product.availability_status = status
      product.active = status === 'hidden' ? 0 : 1
      if (this.productForm.id_token === product.id_token) {
        this.productForm.availability_status = status
        this.productForm.active = status !== 'hidden'
      }
      this.addPending('pendingProductTokens', product.id_token)
      try {
        await setProductAvailability(product.id_token, status)
      } catch (error) {
        product.availability_status = oldStatus
        product.active = oldActive
        if (this.productForm.id_token === product.id_token) {
          this.productForm.availability_status = oldStatus
          this.productForm.active = Boolean(Number(oldActive))
        }
        alert(this.errorMessage(error, 'No se pudo cambiar la disponibilidad'))
      } finally {
        this.removePending('pendingProductTokens', product.id_token)
      }
    },
    async persistProductOrder() {
      if (!this.selectedCategory) return
      try {
        await reorderProducts(this.selectedCategory.id_token, this.selectedCategory.products.map((product) => product.id_token))
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar el orden de los productos'))
        await this.loadMenu(true, true)
      }
    },
    newFeature() {
      this.selectedFeatureToken = ''
      this.featureForm = emptyFeature()
      this.clearFeaturePreview()
    },
    selectFeature(feature) {
      this.selectedFeatureToken = feature.id_token
      this.featureForm = this.cloneFeature(feature)
      this.clearFeaturePreview(false)
      this.featurePreviewUrl = this.featureForm.image_url || ''
    },
    onFeaturePhotoChange(event) {
      const file = event.target.files && event.target.files[0]
      this.clearFeaturePreview()
      this.featurePhotoFile = file || null
      if (file) this.featurePreviewUrl = URL.createObjectURL(file)
    },
    clearFeaturePreview(clearRemote = true) {
      if (this.featurePreviewUrl && this.featurePreviewUrl.startsWith('blob:')) URL.revokeObjectURL(this.featurePreviewUrl)
      this.featurePhotoFile = null
      this.featurePreviewUrl = clearRemote ? '' : this.featurePreviewUrl
    },
    async saveFeatureData() {
      if (!this.featureForm.name.trim()) return alert('Escribe el nombre de la card')
      this.saving = true
      try {
        const payload = {
          ...this.featureForm,
          display_duration_seconds: Math.max(3, Math.min(60, Number(this.featureForm.display_duration_seconds) || 8)),
          active: this.featureForm.active ? 1 : 0
        }
        let token = this.featureForm.id_token
        if (token) await updateFeature(token, payload)
        else {
          const response = await createFeature(payload)
          token = response.id_token
          this.selectedFeatureToken = token
        }
        if (this.featurePhotoFile) await uploadFeaturePhoto(token, this.featurePhotoFile)
        this.featurePhotoFile = null
        await this.loadMenu(true)
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar la card'))
      } finally {
        this.saving = false
      }
    },
    async removeFeature() {
      if (!this.featureForm.id_token || !confirm('Eliminar esta card de promocion?')) return
      await deleteFeature(this.featureForm.id_token)
      this.newFeature()
      await this.loadMenu(true)
    },
    async removeFeaturePhoto() {
      if (!this.featureForm.id_token) return
      await updateFeature(this.featureForm.id_token, { image_url: '' })
      this.featureForm.image_url = ''
      this.clearFeaturePreview()
      await this.loadMenu(true)
    },
    async toggleFeatureCard(feature, checked) {
      const oldValue = Number(feature.active) === 1
      const nextValue = checked ? 1 : 0
      feature.active = nextValue
      if (this.featureForm.id_token === feature.id_token) this.featureForm.active = checked
      this.addPending('pendingFeatureTokens', feature.id_token)
      try {
        await toggleFeature(feature.id_token, nextValue)
      } catch (error) {
        feature.active = oldValue ? 1 : 0
        if (this.featureForm.id_token === feature.id_token) this.featureForm.active = oldValue
        alert(this.errorMessage(error, 'No se pudo cambiar la visibilidad'))
      } finally {
        this.removePending('pendingFeatureTokens', feature.id_token)
      }
    },
    async persistFeatureOrder() {
      try {
        await reorderFeatures(this.features.map((feature) => feature.id_token))
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar el orden de las cards'))
        await this.loadMenu(true, true)
      }
    },
    addRecipeItem() {
      this.recipeForm.items.push({ quantity: '', measure: '', item_name: '' })
    },
    removeRecipeItem(index) {
      this.recipeForm.items.splice(index, 1)
    },
    async saveRecipeData() {
      if (!this.selectedProductToken) return
      this.saving = true
      try {
        await saveRecipe(this.selectedProductToken, this.recipeForm)
        await this.loadMenu(true)
      } catch (error) {
        alert(this.errorMessage(error, 'No se pudo guardar la receta'))
      } finally {
        this.saving = false
      }
    },
    productPriceLabel(product) {
      const values = []
      if (product.has_hot && product.price_hot !== null) values.push(`Cal ${this.money(product.price_hot)}`)
      if (product.has_cold && product.price_cold !== null) values.push(`Frio ${this.money(product.price_cold)}`)
      if (product.price !== null) values.push(this.money(product.price))
      return values.join(' / ') || 'Sin precio'
    },
    featurePriceLabel(feature) {
      if (feature.price_label) return feature.price_label
      if (feature.price !== null && feature.price !== '') return this.money(feature.price)
      return 'Sin precio'
    },
    categoryLocationLabel(cat) {
      const labels = { left: 'Principal', right: 'Secundario', bottom: 'Inferior', full: 'Ancho completo' }
      return labels[cat.section_key] || 'Sin ubicacion'
    },
    money(value) {
      const n = Number(value)
      return Number.isNaN(n) ? value : `$${n.toFixed(n % 1 === 0 ? 0 : 2)}`
    },
    isPending(list, token) {
      return list.includes(token)
    },
    addPending(field, token) {
      if (!this[field].includes(token)) this[field].push(token)
    },
    removePending(field, token) {
      this[field] = this[field].filter((item) => item !== token)
    },
    errorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.error) || fallback
    }
  }
}
</script>

<style scoped>
.admin-menu { min-height: 100vh; padding: 26px; overflow: visible; }
.admin-shell { width: min(1480px, 100%); margin: 0 auto; padding: 26px; }
.admin-head { display: flex; justify-content: space-between; gap: 18px; align-items: center; padding-bottom: 20px; border-bottom: 1px dashed var(--afro-line); text-align: left; }
.eyebrow { margin: 0 0 6px; color: var(--afro-gold); font-weight: 900; letter-spacing: .16em; text-transform: uppercase; }
h1 { margin: 0; font-family: var(--afro-text-serif); font-size: clamp(34px, 4vw, 56px); color: var(--afro-ink); }
.admin-head span { color: var(--afro-olive); font-weight: 700; }
.head-actions, .button-row, .panel-title { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.panel-title { justify-content: space-between; margin-bottom: 16px; }
.panel-title h2 { margin: 0; font-family: var(--afro-text-serif); color: var(--afro-olive); font-size: 28px; }
.helper-text { margin: 6px 0 0; color: var(--afro-olive); font-weight: 700; }
.drag-help, .field-help { display: block; margin-top: 5px; color: rgba(38,51,31,.62); font-size: 12px; font-weight: 800; line-height: 1.35; }
.admin-grid { display: grid; grid-template-columns: 350px 1fr; gap: 20px; margin-top: 22px; align-items: start; }
.category-panel { position: sticky; top: 76px; text-align: left; max-height: calc(100vh - 110px); overflow: auto; }
.category-list, .product-list, .feature-list { display: grid; gap: 10px; }
.catalog-pill { position: relative; min-width: 0; border: 1px solid rgba(38,51,31,.18); background: rgba(255,248,230,.68); border-radius: 16px; padding: 11px; color: var(--afro-ink); display: grid; grid-template-columns: 28px minmax(0, 1fr); align-items: center; gap: 9px; cursor: pointer; transition: opacity .16s ease, transform .16s ease, background .16s ease; }
.catalog-pill:hover { transform: translateY(-1px); }
.catalog-pill.active { background: var(--afro-olive); color: var(--afro-paper-light); }
.catalog-pill.off { opacity: .58; }
.catalog-pill.soldout { border-color: rgba(185,95,87,.55); }
.drag-handle { width: 28px; height: 42px; border: 0; border-radius: 10px; background: rgba(38,51,31,.1); color: currentColor; cursor: grab; font-weight: 1000; letter-spacing: -4px; padding: 0 5px 0 0; touch-action: none; }
.drag-handle:active { cursor: grabbing; }
.active .drag-handle { background: rgba(255,255,255,.12); }
.pill-copy { min-width: 0; display: grid; gap: 4px; }
.pill-copy strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 900; }
.pill-copy small { opacity: .76; line-height: 1.25; }
.binary-switch { grid-column: 2; justify-self: start; display: inline-flex; align-items: center; gap: 7px; cursor: pointer; }
.binary-switch input { position: absolute; opacity: 0; pointer-events: none; }
.switch-track { width: 38px; height: 22px; padding: 3px; border-radius: 999px; background: rgba(76,61,44,.32); transition: background .16s ease; }
.switch-thumb { display: block; width: 16px; height: 16px; border-radius: 50%; background: #fff8e9; box-shadow: 0 2px 5px rgba(0,0,0,.22); transition: transform .16s ease; }
.binary-switch input:checked + .switch-track { background: #4f8656; }
.binary-switch input:checked + .switch-track .switch-thumb { transform: translateX(16px); }
.switch-text { font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: .04em; }
.binary-switch.busy { opacity: .55; }
.status-switch { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; padding: 4px; border-radius: 12px; background: rgba(38,51,31,.09); }
.status-switch button { min-width: 0; border: 0; border-radius: 9px; padding: 7px 4px; background: transparent; color: currentColor; cursor: pointer; font-size: 10px; font-weight: 900; }
.status-switch button.selected.visible { background: #4f8656; color: #fff; }
.status-switch button.selected.sold_out { background: #b95f57; color: #fff; }
.status-switch button.selected.hidden { background: #6f675d; color: #fff; }
.status-switch button:disabled { cursor: wait; opacity: .65; }
.drag-ghost { opacity: .25; }
.drag-chosen { box-shadow: 0 16px 34px rgba(20,26,16,.24); }
.editor-stack { display: grid; gap: 20px; }
.editor-card { text-align: left; }
.form-grid { display: grid; gap: 14px; margin-bottom: 14px; }
.form-grid.cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.form-grid.cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.check-row { display: flex; align-items: center; gap: 10px; font-weight: 900; color: var(--afro-olive); padding-top: 24px; }
.products-layout, .feature-layout-admin { display: grid; grid-template-columns: 360px 1fr; gap: 18px; align-items: start; }
.product-editor, .feature-editor { background: rgba(255,255,255,.26); border-radius: 18px; padding: 16px; border: 1px solid rgba(38,51,31,.14); }
.product-editor h3, .feature-editor h3 { margin: 0 0 14px; font-family: var(--afro-text-serif); color: var(--afro-ink); }
.recipe-card .big { min-height: 180px; }
.afro-textarea.compact { min-height: 86px; }
.recipe-table { display: grid; gap: 8px; margin-bottom: 14px; }
.recipe-head, .recipe-row { display: grid; grid-template-columns: 120px 140px 1fr 90px; gap: 10px; align-items: center; }
.recipe-head { color: var(--afro-olive); font-weight: 900; text-transform: uppercase; font-size: 12px; letter-spacing: .08em; }
.photo-grid { display: grid; grid-template-columns: 1fr 170px; gap: 14px; align-items: stretch; margin: 14px 0; }
.photo-preview { min-height: 120px; border: 1px dashed rgba(38,51,31,.35); border-radius: 16px; display: grid; place-items: center; overflow: hidden; background: rgba(255,248,230,.45); color: var(--afro-olive); font-weight: 900; }
.photo-preview img { width: 100%; height: 100%; object-fit: cover; }
.photo-preview.empty { padding: 14px; text-align: center; }
.empty-admin { color: var(--afro-olive); font-weight: 800; }
.state-card { padding: 30px; text-align: center; }
@media (max-width: 1100px) {
  .admin-grid, .products-layout, .feature-layout-admin { grid-template-columns: 1fr; }
  .category-panel { position: static; max-height: none; }
  .form-grid.cols-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 720px) {
  .admin-menu { padding: 0; }
  .admin-shell { border-radius: 0; padding: 16px; }
  .admin-head { align-items: flex-start; flex-direction: column; }
  .form-grid.cols-2, .form-grid.cols-3, .recipe-head, .recipe-row, .photo-grid { grid-template-columns: 1fr; }
  .recipe-head { display: none; }
  .products-layout, .feature-layout-admin { gap: 14px; }
}

/* Línea gráfica Juliam Arcade */
.admin-head { border-bottom: 1px solid var(--juliam-line); }
.eyebrow { color: var(--juliam-lime); font: 900 10px/1 var(--juliam-mono); }
h1 { color: var(--juliam-text); font-family: var(--juliam-sans); letter-spacing: -.055em; text-transform: uppercase; }
.admin-head span, .helper-text, .drag-help, .field-help { color: var(--juliam-muted); }
.panel-title h2 { color: var(--juliam-cyan); font-family: var(--juliam-sans); font-size: 24px; letter-spacing: -.03em; text-transform: uppercase; }
.catalog-pill { border-color: rgba(255,255,255,.1); border-radius: 10px; background: rgba(5,8,19,.58); color: var(--juliam-text); }
.catalog-pill.active { border-color: rgba(76,245,255,.5); background: rgba(76,245,255,.09); color: var(--juliam-cyan); }
.catalog-pill.soldout { border-color: rgba(255,63,216,.48); }
.drag-handle, .active .drag-handle { background: rgba(255,255,255,.045); color: var(--juliam-muted); }
.switch-track { background: rgba(255,255,255,.14); }
.switch-thumb { background: var(--juliam-muted); }
.binary-switch input:checked + .switch-track { background: rgba(167,255,79,.4); }
.binary-switch input:checked + .switch-track .switch-thumb { background: var(--juliam-lime); }
.status-switch { border-radius: 8px; background: rgba(255,255,255,.04); }
.status-switch button { color: var(--juliam-muted); }
.status-switch button.selected.visible { background: rgba(167,255,79,.17); color: var(--juliam-lime); }
.status-switch button.selected.sold_out { background: rgba(255,63,216,.16); color: var(--juliam-pink); }
.status-switch button.selected.hidden { background: rgba(148,157,184,.13); color: var(--juliam-muted); }
.product-editor, .feature-editor { border-color: rgba(255,255,255,.1); border-radius: 12px; background: rgba(5,8,19,.48); }
.product-editor h3, .feature-editor h3 { color: var(--juliam-text); font-family: var(--juliam-sans); }
.check-row, .recipe-head, .empty-admin { color: var(--juliam-muted); }
.photo-preview { border-color: rgba(76,245,255,.28); border-radius: 10px; background: rgba(5,8,19,.55); color: var(--juliam-muted); }
.state-card { color: var(--juliam-cyan); font-family: var(--juliam-mono); }
</style>
